import { API_CONFIG } from '../api/config';
import { httpService } from '../api/http';
import {
  buildDisplayFromParts,
  buildCountryOptions,
  normalizeFirstLevelRegionName,
  normalizeCountryCode,
  resolveUsStateCode,
} from '../../utils/location';

const DEFAULT_COUNTRY_CODE = 'US';
const DEFAULT_LIMIT = 8;
const MAX_LIMIT = 10;
const SUPPORTED_COUNTRY_FILTER_CODES = new Set([DEFAULT_COUNTRY_CODE]);

const GEOCODING_ENDPOINTS = Object.freeze({
  FORWARD: API_CONFIG.ENDPOINTS.GEOCODING.FORWARD,
  REVERSE: API_CONFIG.ENDPOINTS.GEOCODING.REVERSE,
  AUTOCOMPLETE: API_CONFIG.ENDPOINTS.GEOCODING.AUTOCOMPLETE,
});

const COUNTRY_OPTIONS = buildCountryOptions('en');
const COUNTRY_CODE_TO_NAME = new Map(COUNTRY_OPTIONS.map((country) => [country.code, country.name]));
const COUNTRY_NAME_TO_CODE = new Map();

COUNTRY_OPTIONS.forEach(({ code, name }) => {
  COUNTRY_NAME_TO_CODE.set(name.toLowerCase(), code);
});

[
  ['usa', 'US'],
  ['u.s.', 'US'],
  ['u.s.a.', 'US'],
  ['united states of america', 'US'],
  ['uk', 'GB'],
  ['u.k.', 'GB'],
  ['great britain', 'GB'],
].forEach(([alias, code]) => {
  COUNTRY_NAME_TO_CODE.set(alias, code);
});

/**
 * @typedef {Object} GeocodingSuggestion
 * @property {string|null} id
 * @property {string} label
 * @property {number} latitude
 * @property {number} longitude
 * @property {string|null} countryName
 * @property {string|null} countryCode
 * @property {string|null} regionName
 * @property {string|null} cityName
 * @property {string|null} layer
 * @property {string|null} source
 * @property {number|null} confidence
 * @property {Object} raw
 */

/**
 * @typedef {Object} GeocodedLocation
 * @property {string} label
 * @property {string} fullAddress
 * @property {string|null} addressLine
 * @property {string|null} countryName
 * @property {string|null} countryCode
 * @property {string|null} regionName
 * @property {string|null} cityName
 * @property {number} latitude
 * @property {number} longitude
 */

function safeText(value) {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function safeFiniteNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function resolveLimit(limit) {
  if (typeof limit === 'number' && Number.isFinite(limit) && limit > 0) {
    return Math.min(MAX_LIMIT, Math.floor(limit));
  }
  if (typeof limit === 'string' && limit.trim().length > 0) {
    const parsed = Number.parseInt(limit, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.min(MAX_LIMIT, parsed);
    }
  }
  return DEFAULT_LIMIT;
}

function resolveCountryFilterCode(countryCode) {
  const normalizedCode = normalizeCountryCode(countryCode || '');
  if (!normalizedCode) {
    return DEFAULT_COUNTRY_CODE;
  }
  return SUPPORTED_COUNTRY_FILTER_CODES.has(normalizedCode)
    ? normalizedCode
    : DEFAULT_COUNTRY_CODE;
}

function firstDefinedText(values) {
  return values
    .map(safeText)
    .find((value) => value !== null) || null;
}

function matchesAddressPart(value, expected) {
  return Boolean(value && expected) && value.trim().toLowerCase() === expected.trim().toLowerCase();
}

function normalizeAdminAbbreviation(value) {
  const safeValue = safeText(value);
  if (!safeValue) {
    return null;
  }

  const normalized = safeValue.replace(/[^a-z]/gi, '').toUpperCase();
  return normalized || null;
}

function matchesCountryPart(value, { countryName, countryCode } = {}) {
  if (matchesAddressPart(value, countryName)) {
    return true;
  }

  const expectedCountryCode = resolveCountryCode(countryCode, countryName);
  if (!expectedCountryCode) {
    return false;
  }

  return resolveCountryCode(null, value) === expectedCountryCode;
}

function matchesRegionPart(value, { regionName, countryCode } = {}) {
  if (matchesAddressPart(value, regionName)) {
    return true;
  }

  if (normalizeCountryCode(countryCode || '') !== 'US') {
    return false;
  }

  const expectedRegionCode = resolveUsStateCode(regionName);
  if (!expectedRegionCode) {
    return false;
  }

  return normalizeAdminAbbreviation(value) === expectedRegionCode;
}

function resolveAddressLineFromParts(parts, { cityName, regionName, countryName, countryCode } = {}) {
  if (!Array.isArray(parts) || parts.length === 0) {
    return null;
  }

  const remainingParts = [...parts];

  if (remainingParts.length > 0 && matchesCountryPart(remainingParts[remainingParts.length - 1], { countryName, countryCode })) {
    remainingParts.pop();
  }
  if (remainingParts.length > 0 && matchesRegionPart(remainingParts[remainingParts.length - 1], { regionName, countryCode })) {
    remainingParts.pop();
  }
  while (remainingParts.length > 0 && ADMIN_AREA_SEGMENT_PATTERN.test(remainingParts[remainingParts.length - 1])) {
    remainingParts.pop();
  }
  if (remainingParts.length > 0 && matchesAddressPart(remainingParts[remainingParts.length - 1], cityName)) {
    remainingParts.pop();
  }

  const addressLine = remainingParts
    .map((part) => safeText(part))
    .filter(Boolean)
    .join(', ');

  return addressLine || null;
}

const ADMIN_AREA_SEGMENT_PATTERN = /\b(county|parish|borough|municipality|district)\b/i;
const ADDRESS_LAYER_HINTS = new Set([
  'address',
  'street',
  'venue',
]);
const LOCALITY_LAYER_HINTS = new Set([
  'address',
  'street',
  'venue',
  'locality',
  'neighbourhood',
  'neighborhood',
  'borough',
  'macrohood',
  'microhood',
]);
const NON_LOCALITY_LAYER_HINTS = new Set([
  'country',
  'macrocountry',
  'region',
  'macroregion',
  'county',
  'localadmin',
  'coarse',
  'postalcode',
]);

function shouldInferCityFromTwoPartLabel(parts, layer) {
  if (parts.length !== 2) {
    return false;
  }

  const firstSegment = safeText(parts[0]);
  if (!firstSegment) {
    return false;
  }

  const normalizedLayer = safeText(layer)?.toLowerCase();
  if (normalizedLayer) {
    if (LOCALITY_LAYER_HINTS.has(normalizedLayer)) {
      return true;
    }
    if (NON_LOCALITY_LAYER_HINTS.has(normalizedLayer)) {
      return false;
    }
  }

  return !ADMIN_AREA_SEGMENT_PATTERN.test(firstSegment);
}

function looksLikeStreetAddressSegment(value) {
  const segment = safeText(value);
  return Boolean(segment && /\d/.test(segment) && !ADMIN_AREA_SEGMENT_PATTERN.test(segment));
}

function shouldInferStreetCityCountryFromThreePartLabel(parts, layer) {
  if (parts.length !== 3) {
    return false;
  }

  const firstSegment = safeText(parts[0]);
  const secondSegment = safeText(parts[1]);
  if (!firstSegment || !secondSegment) {
    return false;
  }

  const normalizedLayer = safeText(layer)?.toLowerCase();
  if (normalizedLayer) {
    return ADDRESS_LAYER_HINTS.has(normalizedLayer);
  }

  return looksLikeStreetAddressSegment(firstSegment);
}

function resolveCityFromAddressParts(parts, inferCityFromTwoPart = true) {
  if (parts.length <= 1) {
    return null;
  }

  if (parts.length === 2) {
    return inferCityFromTwoPart ? safeText(parts[0]) : null;
  }

  const cityCandidates = parts.slice(0, -2);
  for (let index = cityCandidates.length - 1; index >= 0; index -= 1) {
    const candidate = safeText(cityCandidates[index]);
    if (candidate && !ADMIN_AREA_SEGMENT_PATTERN.test(candidate)) {
      return candidate;
    }
  }

  return null;
}

function splitAddressParts(label, context = {}) {
  const safeLabel = safeText(label);
  if (!safeLabel) {
    return {
      parts: [],
      cityName: null,
      regionName: null,
      countryName: null,
    };
  }

  const parts = safeLabel.split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  const inferCityFromTwoPart = shouldInferCityFromTwoPartLabel(parts, context.layer);
  const inferStreetCityCountryFromThreePart = shouldInferStreetCityCountryFromThreePartLabel(parts, context.layer);
  const countryName = parts.length > 0 ? safeText(parts[parts.length - 1]) : null;
  let regionName = inferStreetCityCountryFromThreePart
    ? null
    : (parts.length > 2 ? safeText(parts[parts.length - 2]) : null);
  const cityName = inferStreetCityCountryFromThreePart
    ? safeText(parts[1])
    : resolveCityFromAddressParts(parts, inferCityFromTwoPart);
  if (parts.length === 2 && !inferCityFromTwoPart) {
    regionName = normalizeFirstLevelRegionName(parts[0]) || null;
  }

  return {
    parts,
    cityName,
    regionName,
    countryName,
  };
}

function pushUniqueText(target, value) {
  const normalizedValue = safeText(value);
  if (!normalizedValue || target.includes(normalizedValue)) {
    return;
  }
  target.push(normalizedValue);
}

function isCountryQueryPart(part, countryCode) {
  const resolvedCountryCode = resolveCountryCode(null, part);
  if (!resolvedCountryCode) {
    return false;
  }

  const normalizedCountryCode = normalizeCountryCode(countryCode || '');
  if (!normalizedCountryCode) {
    return true;
  }

  return resolvedCountryCode === normalizedCountryCode;
}

function buildStructuredForwardFallbackQueries(text, countryCode) {
  const query = safeText(text);
  if (!query) {
    return [];
  }

  const parts = query.split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  if (parts.length <= 1) {
    return [];
  }

  let normalizedParts = parts;
  if (isCountryQueryPart(parts[parts.length - 1], countryCode)) {
    normalizedParts = parts.slice(0, -1);
  }

  normalizedParts = normalizedParts.filter((part, index) => (
    index === 0 || !ADMIN_AREA_SEGMENT_PATTERN.test(part)
  ));

  const candidates = [];
  const simplifiedQuery = normalizedParts.join(', ');

  if (simplifiedQuery && simplifiedQuery !== query) {
    pushUniqueText(candidates, simplifiedQuery);
  }

  const localityParts = normalizedParts.length >= 3
    ? normalizedParts.slice(-2)
    : normalizedParts;

  pushUniqueText(candidates, localityParts.join(', '));
  pushUniqueText(candidates, localityParts[0]);

  return candidates.filter((candidate) => candidate !== query);
}

function resolveCountryCode(candidateCode, countryName) {
  const normalizedCode = normalizeCountryCode(candidateCode || '');
  if (normalizedCode) {
    return normalizedCode;
  }

  const normalizedCountry = safeText(countryName)?.toLowerCase();
  if (!normalizedCountry) {
    return null;
  }

  return COUNTRY_NAME_TO_CODE.get(normalizedCountry) || null;
}

function resolveCountryName(candidateName, countryCode) {
  const normalizedName = safeText(candidateName);
  if (normalizedName) {
    return normalizedName;
  }

  const normalizedCode = normalizeCountryCode(countryCode || '');
  if (!normalizedCode) {
    return null;
  }

  return COUNTRY_CODE_TO_NAME.get(normalizedCode) || normalizedCode;
}

function extractErrorBodyMessage(errorBody) {
  if (typeof errorBody === 'string') {
    const trimmedBody = errorBody.trim();
    if (!trimmedBody) {
      return null;
    }

    try {
      const parsed = JSON.parse(trimmedBody);
      return safeText(parsed?.message);
    } catch {
      return safeText(trimmedBody);
    }
  }

  if (!errorBody || typeof errorBody !== 'object') {
    return null;
  }

  return safeText(errorBody.message);
}

function normalizeGeocodingError(error, fallbackMessage) {
  if (error?.name === 'AbortError') {
    return error;
  }

  if (error?.status === 401 || error?.status === 403) {
    const authError = new Error('Sign in to search locations.');
    authError.status = error.status;
    authError.code = 'GEOCODING_AUTH_REQUIRED';
    return authError;
  }

  const message = extractErrorBodyMessage(error?.body)
    || safeText(error?.message)
    || fallbackMessage;
  const normalized = new Error(message);
  normalized.status = error?.status;
  normalized.code = 'GEOCODING_REQUEST_FAILED';
  return normalized;
}

function normalizeFeature(feature) {
  if (!feature || typeof feature !== 'object') {
    return null;
  }

  const latitude = safeFiniteNumber(feature.latitude);
  const longitude = safeFiniteNumber(feature.longitude);
  const label = safeText(feature.label) || buildDisplayFromParts({
    city: feature.city,
    stateOrRegion: feature.region,
    country: feature.country,
  });

  if (!label || latitude === null || longitude === null) {
    return null;
  }

  const parsedFromLabel = splitAddressParts(label, {
    layer: feature.layer,
  });
  const countryCode = resolveCountryCode(
    firstDefinedText([
      feature.countryCode,
      feature.country_code,
      feature.iso2,
      feature.country_a,
    ]),
    firstDefinedText([
      feature.country,
      feature.countryName,
      parsedFromLabel.countryName,
    ])
  );
  const countryName = resolveCountryName(
    firstDefinedText([
      feature.country,
      feature.countryName,
      parsedFromLabel.countryName,
    ]),
    countryCode
  );

  const cityName = firstDefinedText([
    feature.locality,
    feature.localadmin,
    feature.city,
    feature.town,
    feature.village,
    feature.municipality,
    parsedFromLabel.cityName,
  ]);

  const regionName = firstDefinedText([
    normalizeFirstLevelRegionName(feature.region),
    normalizeFirstLevelRegionName(feature.regionName),
    normalizeFirstLevelRegionName(feature.state),
    normalizeFirstLevelRegionName(feature.stateOrRegion),
    normalizeFirstLevelRegionName(feature.province),
    normalizeFirstLevelRegionName(parsedFromLabel.regionName),
  ]);

  return {
    id: safeText(feature.id),
    label,
    addressLine: resolveAddressLineFromParts(parsedFromLabel.parts, {
      cityName,
      regionName,
      countryName,
      countryCode,
    }),
    latitude,
    longitude,
    countryName,
    countryCode,
    regionName,
    cityName,
    layer: safeText(feature.layer),
    source: safeText(feature.source),
    confidence: safeFiniteNumber(feature.confidence),
    raw: feature,
  };
}

function responseToSuggestions(payload) {
  const results = Array.isArray(payload?.results) ? payload.results : [];
  return results
    .map((result) => normalizeFeature(result))
    .filter(Boolean);
}

function buildEndpointWithQuery(endpoint, queryParams) {
  const params = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined && `${value}`.trim() !== '') {
      params.set(key, `${value}`);
    }
  });

  const query = params.toString();
  return query ? `${endpoint}?${query}` : endpoint;
}

async function requestSuggestions(endpoint, query, signal) {
  const endpointWithQuery = buildEndpointWithQuery(
    endpoint,
    {
      query: query?.text,
      country: resolveCountryFilterCode(query?.countryCode),
      limit: resolveLimit(query?.limit),
    }
  );

  const response = await httpService.get(endpointWithQuery, { signal });
  return responseToSuggestions(response);
}

function shouldAttemptForwardFallback(error) {
  if (error?.name === 'AbortError') {
    return false;
  }

  if (error?.status === 401 || error?.status === 403 || error?.status === 429) {
    return false;
  }

  return typeof error?.status === 'number' ? error.status >= 500 : true;
}

async function requestStructuredForwardFallback(query, signal) {
  const candidates = buildStructuredForwardFallbackQueries(query?.text, query?.countryCode);
  if (candidates.length === 0) {
    return null;
  }

  let sawSuccessfulResponse = false;
  let lastError = null;

  for (const candidate of candidates) {
    try {
      const suggestions = await requestSuggestions(
        GEOCODING_ENDPOINTS.FORWARD,
        {
          ...query,
          text: candidate,
        },
        signal
      );

      sawSuccessfulResponse = true;
      if (suggestions.length > 0) {
        return suggestions;
      }
    } catch (error) {
      if (!shouldAttemptForwardFallback(error)) {
        throw error;
      }
      lastError = error;
    }
  }

  if (sawSuccessfulResponse) {
    return [];
  }

  if (lastError) {
    throw lastError;
  }

  return [];
}

/**
 * @param {{ text: string, countryCode?: string, limit?: number }} query
 * @param {AbortSignal=} signal
 * @returns {Promise<GeocodingSuggestion[]>}
 */
async function forwardAutocomplete(query, signal) {
  const text = safeText(query?.text);
  if (!text) {
    return [];
  }

  try {
    return await requestSuggestions(
      GEOCODING_ENDPOINTS.AUTOCOMPLETE,
      {
        ...query,
        text,
      },
      signal
    );
  } catch (error) {
    if (shouldAttemptForwardFallback(error)) {
      try {
        const fallbackSuggestions = await requestStructuredForwardFallback(
          {
            ...query,
            text,
          },
          signal
        );

        if (fallbackSuggestions !== null) {
          return fallbackSuggestions;
        }
      } catch (fallbackError) {
        throw normalizeGeocodingError(fallbackError, 'Unable to load address suggestions.');
      }
    }

    throw normalizeGeocodingError(error, 'Unable to load address suggestions.');
  }
}

/**
 * @param {{ text: string, countryCode?: string, limit?: number }} query
 * @param {AbortSignal=} signal
 * @returns {Promise<GeocodingSuggestion[]>}
 */
async function forwardLookup(query, signal) {
  const text = safeText(query?.text);
  if (!text) {
    return [];
  }

  try {
    return await requestSuggestions(
      GEOCODING_ENDPOINTS.FORWARD,
      {
        ...query,
        text,
      },
      signal
    );
  } catch (error) {
    throw normalizeGeocodingError(error, 'Unable to resolve the address.');
  }
}

/**
 * @param {{ lat: number, lon: number, limit?: number }} query
 * @param {AbortSignal=} signal
 * @returns {Promise<GeocodedLocation|null>}
 */
async function reverseLookup(query, signal) {
  const latitude = safeFiniteNumber(query?.lat);
  const longitude = safeFiniteNumber(query?.lon);

  if (latitude === null || longitude === null) {
    throw new Error('Latitude and longitude are required for reverse geocoding.');
  }

  const endpoint = buildEndpointWithQuery(
    GEOCODING_ENDPOINTS.REVERSE,
    {
      lat: latitude,
      lon: longitude,
      limit: resolveLimit(query?.limit),
    }
  );

  try {
    const response = await httpService.get(endpoint, { signal });
    const suggestions = responseToSuggestions(response);
    if (suggestions.length === 0) {
      return null;
    }
    return toGeocodedLocation(suggestions[0]);
  } catch (error) {
    throw normalizeGeocodingError(error, 'Unable to resolve your coordinates.');
  }
}

/**
 * @param {GeocodingSuggestion|Object} suggestion
 * @returns {GeocodedLocation|null}
 */
function toGeocodedLocation(suggestion) {
  const normalized = normalizeFeature(suggestion?.raw || suggestion);
  if (!normalized) {
    return null;
  }

  const fallbackLabel = buildDisplayFromParts({
    city: normalized.cityName,
    stateOrRegion: normalized.regionName,
    country: normalized.countryName,
  });

  const label = normalized.label || fallbackLabel || `${normalized.latitude}, ${normalized.longitude}`;

  return {
    label,
    fullAddress: label,
    addressLine: normalized.addressLine || null,
    countryName: normalized.countryName,
    countryCode: normalized.countryCode,
    regionName: normalized.regionName,
    cityName: normalized.cityName,
    latitude: normalized.latitude,
    longitude: normalized.longitude,
  };
}

export const geocodingClient = Object.freeze({
  endpoints: GEOCODING_ENDPOINTS,
  forwardAutocomplete,
  forwardLookup,
  reverseLookup,
  toGeocodedLocation,
  normalizeFeature,
});
